/**
 * JsonEditor - Structured key-value editor for JSON fields
 *
 * Renders JSON objects as a form-like UI where:
 * - Object keys become read-only labels
 * - Object values become editable inputs (type-appropriate)
 * - Nested objects are rendered as collapsible groups
 * - Arrays are rendered as editable lists
 */

import { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  Sheet,
  Button,
  Typography,
  IconButton,
  Switch,
  FormControl,
  FormLabel,
  Chip,
  Stack,
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Textarea,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";

interface JsonEditorProps extends GridRenderEditCellParams {
  nullable?: boolean;
  targetTable?: string;
  targetField?: string;
}

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

/**
 * Determine the appropriate input type for a value
 */
function getValueType(
  value: JsonValue,
): "string" | "number" | "boolean" | "null" | "object" | "array" {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  return "string";
}

/**
 * Format a key name for display (snake_case to Title Case)
 */
function formatKeyLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Recursive component for rendering JSON values
 */
interface JsonFieldProps {
  keyPath: string;
  label: string;
  value: JsonValue;
  onChange: (keyPath: string, newValue: JsonValue) => void;
  onDelete?: () => void;
  depth?: number;
}

function JsonField({
  keyPath,
  label,
  value,
  onChange,
  onDelete,
  depth = 0,
}: JsonFieldProps) {
  const valueType = getValueType(value);

  const handlePrimitiveChange = useCallback(
    (newValue: JsonValue) => {
      onChange(keyPath, newValue);
    },
    [keyPath, onChange],
  );

  const handleNestedChange = useCallback(
    (nestedPath: string, newValue: JsonValue) => {
      onChange(nestedPath, newValue);
    },
    [onChange],
  );

  switch (valueType) {
    case "string":
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography
            level="body-sm"
            sx={{ minWidth: 120, fontWeight: 500, color: "text.secondary" }}
          >
            {label}:
          </Typography>
          <Input
            size="sm"
            value={value as string}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            sx={{ flex: 1 }}
          />
          {onDelete && (
            <IconButton size="sm" color="danger" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );

    case "number":
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography
            level="body-sm"
            sx={{ minWidth: 120, fontWeight: 500, color: "text.secondary" }}
          >
            {label}:
          </Typography>
          <Input
            size="sm"
            type="number"
            value={value as number}
            onChange={(e) =>
              handlePrimitiveChange(parseFloat(e.target.value) || 0)
            }
            sx={{ flex: 1 }}
          />
          {onDelete && (
            <IconButton size="sm" color="danger" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );

    case "boolean":
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography
            level="body-sm"
            sx={{ minWidth: 120, fontWeight: 500, color: "text.secondary" }}
          >
            {label}:
          </Typography>
          <FormControl orientation="horizontal">
            <Switch
              checked={value as boolean}
              onChange={(e) => handlePrimitiveChange(e.target.checked)}
              size="sm"
            />
            <FormLabel sx={{ ml: 1 }}>{value ? "Yes" : "No"}</FormLabel>
          </FormControl>
          {onDelete && (
            <IconButton size="sm" color="danger" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );

    case "null":
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography
            level="body-sm"
            sx={{ minWidth: 120, fontWeight: 500, color: "text.secondary" }}
          >
            {label}:
          </Typography>
          <Chip size="sm" variant="outlined">
            null
          </Chip>
          {onDelete && (
            <IconButton size="sm" color="danger" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );

    case "object": {
      const objValue = value as JsonObject;
      return (
        <AccordionGroup sx={{ mb: 1 }}>
          <Accordion defaultExpanded={depth < 2}>
            <AccordionSummary>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography level="body-sm" fontWeight={600}>
                  {label}
                </Typography>
                <Chip size="sm" variant="outlined">
                  {Object.keys(objValue).length} fields
                </Chip>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                {Object.entries(objValue).map(([key, val]) => (
                  <JsonField
                    key={key}
                    keyPath={`${keyPath}.${key}`}
                    label={formatKeyLabel(key)}
                    value={val}
                    onChange={handleNestedChange}
                    onDelete={() => {
                      const newObj = { ...objValue };
                      delete newObj[key];
                      onChange(keyPath, newObj);
                    }}
                    depth={depth + 1}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>
      );
    }

    case "array": {
      const arrValue = value as JsonArray;
      return (
        <AccordionGroup sx={{ mb: 1 }}>
          <Accordion defaultExpanded={depth < 2}>
            <AccordionSummary>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography level="body-sm" fontWeight={600}>
                  {label}
                </Typography>
                <Chip size="sm" variant="outlined">
                  {arrValue.length} items
                </Chip>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1} sx={{ pl: 2 }}>
                {arrValue.map((item, index) => (
                  <JsonField
                    key={index}
                    keyPath={`${keyPath}[${index}]`}
                    label={`Item ${index + 1}`}
                    value={item}
                    onChange={handleNestedChange}
                    onDelete={() => {
                      const newArr = [...arrValue];
                      newArr.splice(index, 1);
                      onChange(keyPath, newArr);
                    }}
                    depth={depth + 1}
                  />
                ))}
                <Button
                  size="sm"
                  variant="plain"
                  startDecorator={<AddIcon />}
                  onClick={() => {
                    const newArr = [...arrValue, ""];
                    onChange(keyPath, newArr);
                  }}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Add Item
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>
      );
    }

    default:
      return null;
  }
}

export default function JsonEditor({
  id,
  value,
  field,
  nullable,
}: JsonEditorProps) {
  const apiRef = useGridApiContext();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(true);
  const isClosingRef = useRef(false);

  // Parse the JSON value
  const [localValue, setLocalValue] = useState<Record<string, unknown>>(() => {
    if (value === null || value === undefined) return {};
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === "object" && !Array.isArray(parsed)
          ? parsed
          : {};
      } catch {
        return {};
      }
    }
    if (typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    return {};
  });

  // Handle value changes
  const handleChange = useCallback((keyPath: string, newValue: JsonValue) => {
    setLocalValue((current) => {
      if (keyPath === "root") {
        return typeof newValue === "object" && !Array.isArray(newValue)
          ? (newValue as Record<string, unknown>)
          : current;
      }

      const pathParts = keyPath
        .replace(/^root\.?/, "")
        .split(/\.|\[|\]/)
        .filter(Boolean);
      if (pathParts.length === 0) {
        return typeof newValue === "object" && !Array.isArray(newValue)
          ? (newValue as Record<string, unknown>)
          : current;
      }

      const updated = JSON.parse(JSON.stringify(current));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let target: any = updated;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        const index = parseInt(part, 10);
        target = isNaN(index) ? target[part] : target[index];
      }
      const lastPart = pathParts[pathParts.length - 1];
      const lastIndex = parseInt(lastPart, 10);
      if (isNaN(lastIndex)) {
        target[lastPart] = newValue;
      } else {
        target[lastIndex] = newValue;
      }

      return updated;
    });
  }, []);

  // Update cell value when localValue changes
  useEffect(() => {
    apiRef.current.setEditCellValue({ id, field, value: localValue });
  }, [localValue, apiRef, id, field]);

  const handleSave = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setOpen(false);
    setTimeout(() => {
      try {
        apiRef.current.stopCellEditMode({ id, field });
      } catch {
        // Ignore - cell might already be out of edit mode
      } finally {
        isClosingRef.current = false;
      }
    }, 0);
  }, [apiRef, id, field]);

  const handleCancel = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setOpen(false);
    setTimeout(() => {
      try {
        apiRef.current.stopCellEditMode({
          id,
          field,
          ignoreModifications: true,
        });
      } catch {
        // Ignore
      } finally {
        isClosingRef.current = false;
      }
    }, 0);
  }, [apiRef, id, field]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCancel();
      }
    },
    [handleCancel],
  );

  if (!open) return null;

  return (
    <Box
      ref={anchorRef}
      sx={{ position: "relative", width: "100%", height: "100%" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          zIndex: 1300,
          p: 2,
          width: 450,
          maxWidth: "95vw",
          maxHeight: "60vh",
          overflow: "auto",
          borderRadius: "md",
          boxShadow: "lg",
        }}
        onKeyDown={handleKeyDown}
      >
        <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
          Edit JSON Data
        </Typography>

        {Object.keys(localValue).length > 0 ? (
          Object.entries(localValue).map(([key, val]) => (
            <JsonField
              key={key}
              keyPath={`root.${key}`}
              label={formatKeyLabel(key)}
              value={val as JsonValue}
              onChange={handleChange}
              onDelete={() => {
                const newObj = { ...localValue };
                delete newObj[key];
                setLocalValue(newObj);
              }}
            />
          ))
        ) : (
          <Textarea
            value={JSON.stringify(localValue, null, 2)}
            onChange={(e) => {
              try {
                setLocalValue(JSON.parse(e.target.value));
              } catch {
                // Invalid JSON
              }
            }}
            placeholder={nullable ? "null" : "Enter JSON..."}
            minRows={4}
          />
        )}

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
        >
          <Button size="sm" variant="plain" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="solid" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </Sheet>
    </Box>
  );
}
